import * as React from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import {
  BookkeeperCustomerFragment,
  BookkeeperCustomersDocument,
  BookkeepersManagementBookkeeperCompaniesDocument,
  BookkeepersManagementBookkeeperCompany,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import useSearchParams from 'modules/core/hooks/useSearchParams'

import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'

import CompaniesTable from './CompaniesTable'
import CompanyBookkeepers from './CompanyBookkeepers'

interface QueryParams {
  customerId: number
}

const CompanyPreferences = () => {
  const { customerId } = useSearchParams<QueryParams>()
  const isBookkeeperView = useBookkeeperView()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [selectedCustomer, setSelectedCustomer] = React.useState<BookkeeperCustomerFragment | null>(
    null
  )
  const bookkeeperContext = {
    isBookkeeperView: isBookkeeperView,
  }
  const { data: bookkeeperCompaniesData } = useQuery(
    BookkeepersManagementBookkeeperCompaniesDocument,
    {
      context: bookkeeperContext,
    }
  )
  const { data: bookkeeperCustomers, loading: loadingCustomers } = useQuery(
    BookkeeperCustomersDocument,
    {
      context: bookkeeperContext,
      variables: {
        customerName: debouncedSearch,
      },
    }
  )

  useEffect(() => {
    if (!selectedCustomer && !loadingCustomers) {
      const customer =
        bookkeeperCustomers?.bookkeepersManagement.bookkeeperCustomersConnection.nodes.find(
          (bkCustomer) => bkCustomer.customerCompanyId === customerId
        )
      if (customer) setSelectedCustomer(customer)
    }
  }, [customerId, loadingCustomers]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!bookkeeperCompaniesData || !bookkeeperCustomers) {
    return (
      <Box width="full" height="full" alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  const customers: BookkeeperCustomerFragment[] =
    bookkeeperCustomers.bookkeepersManagement.bookkeeperCustomersConnection.nodes
  const bookkeeperCompanies: BookkeepersManagementBookkeeperCompany[] =
    bookkeeperCompaniesData.bookkeepersManagement.bookkeeperCompaniesConnection.nodes

  const bookkeeperCompany = bookkeeperCompanies.find(({ active }) => active)

  if (!bookkeeperCompany) return <NotFound />

  return (
    <>
      <Box width="full" background="grey000White" paddingX="s48" paddingY="s32">
        <CompaniesTable
          customers={customers}
          setSelectedCustomer={setSelectedCustomer}
          search={search}
          onSearch={onSearch}
        />
      </Box>
      {selectedCustomer && (
        <CompanyBookkeepers
          bookkeeperCompanyId={bookkeeperCompany.companyId}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
        />
      )}
    </>
  )
}

export default CompanyPreferences
